import React, { useEffect, useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";

const ScriptManager: React.FC = () => {
  const projectId = useCurrentProjectId();
  const defaultScript = "<script></script>";

  const [script, setScript] = useState<string | null>(null);

  const { data: scriptData, isLoading: isScriptLoading } =
    trpc.project.getScript.useQuery(
      { projectId: projectId || "" },
      { enabled: Boolean(projectId) },
    );

  useEffect(() => {
    if (scriptData) {
      setScript(scriptData.script !== null ? scriptData.script : defaultScript);
    }
  }, [scriptData]);

  const updateProjectScript = trpc.project.createOrUpdateScript.useMutation();

  const handleSave = async () => {
    if (!projectId || script === null) {
      return;
    }

    try {
      await updateProjectScript.mutateAsync({
        projectId,
        script,
      });
    } catch (error) {
      console.error("Failed to save script:", error);
    }
  };

  return (
    <div className="flex w-[616px] flex-col gap-4">
      <Card className="h-full w-full gap-4 p-5">
        <CardHeader>
          <CardTitle className="mb-1">Script Manager</CardTitle>
          <CardDescription className="text-muted">
            Your custom script will be injected into all your pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          {isScriptLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <Textarea
              value={script ?? defaultScript}
              onChange={setScript}
              textLength="long"
              UNSAFE_className="h-full w-full min-h-[300px] text-placeholder"
              font="mono"
            />
          )}
        </CardContent>
        <div className="h-px w-full bg-border" />
        <CardFooter className="justify-end p-0">
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              void handleSave();
            }}
            disabled={
              updateProjectScript.isPending ||
              isScriptLoading ||
              script === null
            }
          >
            {updateProjectScript.isPending ? "Saving..." : "Save"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ScriptManager;
